import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Categories.css";

class Categories extends Component {
  state = {
    categories: [],
    formData: {
      id: null,
      category_name: "",
      category_status: "1",
      category_img: null,
    },
    imagePreview: "",
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchCategories();
  }

  fetchCategories = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const res = await axios.get("/products/categories");
      const data = res.data?.data || [];

      this.setState({
        categories: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load categories.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: "",
    }));
  };

  handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      this.setState((prev) => ({
        formData: { ...prev.formData, category_img: file },
        imagePreview: URL.createObjectURL(file),
        error: "",
      }));
    }
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", currentPage: 1 });
  };

  openModal = (mode = "add", category = null) => {
    const modalElement = document.getElementById("categoryModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    this.setState(
      {
        modalMode: mode,
        formData:
          mode === "edit" && category
            ? {
                id: category.id,
                category_name: category.category_name || "",
                category_status: category.category_status?.toString() || "1",
                category_img: null,
              }
            : {
                id: null,
                category_name: "",
                category_status: "1",
                category_img: null,
              },
        imagePreview:
          mode === "edit" && category && category.category_img
            ? `${baseUrl}/uploads/categories/${category.category_img}`
            : "",
        error: "",
      },
      () => modal.show()
    );
  };

  closeModal = () => {
    const modalElement = document.getElementById("categoryModal");
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        category_name: "",
        category_status: "1",
        category_img: null,
      },
      imagePreview: "",
      error: "",
      submitting: false,
      modalMode: "add",
    });
    const fileInput = document.getElementById("categoryImage");
    if (fileInput) fileInput.value = "";
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;
    const categoryName = formData.category_name.trim();

    if (!categoryName) {
      return this.setState({ error: "Category name is required." });
    }

    this.setState({ submitting: true, error: "" });

    const data = new FormData();
    data.append("category_name", categoryName);
    data.append("category_status", formData.category_status);
    if (formData.category_img) {
      data.append("category_img", formData.category_img);
    }

    try {
      const token = localStorage.getItem("token");
      const headers = {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      };

      if (modalMode === "add") {
        await axios.post("/products/categories", data, { headers });
      } else {
        await axios.post(`/products/categories/${formData.id}?_method=PUT`, data, { headers });
      }

      await this.fetchCategories();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Category added!" : "Category updated!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving category.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`/products/categories/${deleteId}`);
      await this.fetchCategories();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Category deleted!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed to delete category.",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredCategories = () => {
    const { categories, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return categories.filter((cat) => {
      const name = (cat.category_name || "").toLowerCase();
      const status = cat.category_status?.toString() || "0";

      const matchesSearch = !keyword || name.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(this.getFilteredCategories().length / itemsPerPage));
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  render() {
    const {
      categories,
      formData,
      imagePreview,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredCategories = this.getFilteredCategories();
    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentCategories = filteredCategories.slice(startIndex, startIndex + itemsPerPage);
    const startRecord = filteredCategories.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredCategories.length);

    const activeCount = categories.filter((c) => c.category_status?.toString() === "1").length;
    const inactiveCount = categories.length - activeCount;
    const pageNumbers = this.getPageNumbers(totalPages);
    const baseUrl = axios.defaults.baseURL.replace("/api", "");

    return (
      <div className="pc-container categories-page">
        <div className="pc-content">
          <div className="categories-shell">
            {/* Header */}
            <div className="categories-topbar">
              <div>
                <div className="categories-breadcrumb">
                  <span>Catalog</span>
                  <i className="ph ph-caret-right" />
                  <strong>Categories</strong>
                </div>
                <h2 className="categories-title">Categories</h2>
                <p className="categories-subtitle">
                  Manage product categories, media, and availability status.
                </p>
              </div>

              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-plus" />
                <span>New Category</span>
              </button>
            </div>

            {/* Summary */}
            <div className="category-summary-grid">
              <div className="category-summary-card">
                <div className="category-summary-icon blue">
                  <i className="ph ph-folders" />
                </div>
                <div>
                  <span className="summary-label">Total Categories</span>
                  <strong>{categories.length}</strong>
                </div>
              </div>

              <div className="category-summary-card">
                <div className="category-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="category-summary-card">
                <div className="category-summary-icon pink">
                  <i className="ph ph-pause-circle" />
                </div>
                <div>
                  <span className="summary-label">Inactive</span>
                  <strong>{inactiveCount}</strong>
                </div>
              </div>
            </div>

            {/* Main card */}
            <div className="category-card">
              <div className="category-card-header">
                <div>
                  <h4>Category List</h4>
                  <p>
                    {filteredCategories.length} result
                    {filteredCategories.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="category-toolbar">
                <div className="category-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="category-search"
                    placeholder="Search category name..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="category-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="category-filter-group">
                  <label htmlFor="catStatusFilter">Status</label>
                  <select
                    id="catStatusFilter"
                    className="category-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="category-filter-group">
                  <label htmlFor="catRows">Rows</label>
                  <select
                    id="catRows"
                    className="category-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="category-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="category-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchCategories}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="category-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading categories...</span>
                </div>
              ) : (
                <>
                  <div className="category-table-wrap">
                    <table className="category-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th className="col-image">Image</th>
                          <th>Category Name</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCategories.length ? (
                          currentCategories.map((cat, index) => {
                            const isActive = cat.category_status?.toString() === "1";
                            return (
                              <tr key={cat.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>
                                <td className="col-image">
                                  <div className="category-img-wrap">
                                    {cat.category_img ? (
                                      <img
                                        src={`${baseUrl}/uploads/categories/${cat.category_img}`}
                                        alt={cat.category_name}
                                        loading="lazy"
                                      />
                                    ) : (
                                      <div className="category-img-placeholder">
                                        <i className="ph ph-image" />
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="category-name-cell">
                                    <div>
                                      <span className="category-name">
                                        {cat.category_name}
                                      </span>
                                      <small>ID #{cat.id}</small>
                                    </div>
                                  </div>
                                </td>
                                <td className="col-status">
                                  <span
                                    className={`category-status-badge ${
                                      isActive ? "active" : "inactive"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="category-actions">
                                    <button
                                      type="button"
                                      className="category-action-btn edit"
                                      onClick={() => this.openModal("edit", cat)}
                                      title="Edit category"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="category-action-btn delete"
                                      onClick={() => this.confirmDelete(cat.id)}
                                      title="Delete category"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="5">
                              <div className="category-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-folders" />
                                </div>
                                <h5>No categories found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search or filters."
                                    : "Add your first category to get started."}
                                </p>
                                {searchTerm || statusFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="category-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Category
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredCategories.length > 0 && (
                    <div className="category-pagination-wrap">
                      <div className="category-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredCategories.length}</strong> categories
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Category pagination">
                          <ul className="category-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={safeCurrentPage === page ? "active" : ""}
                                >
                                  <button type="button" onClick={() => this.goToPage(page)}>
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li className={safeCurrentPage === totalPages ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="categoryModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content category-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header category-modal-header">
                  <div>
                    <span className="category-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Category" : "Edit Category"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="category-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body category-modal-body">
                  {error && (
                    <div className="category-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="category-form-group">
                    <label htmlFor="categoryName">
                      Category Name <span>*</span>
                    </label>
                    <div className="category-input-wrap">
                      <i className="ph ph-text-t" />
                      <input
                        id="categoryName"
                        type="text"
                        name="category_name"
                        className="category-input"
                        placeholder="e.g. Electronics, Clothing..."
                        value={formData.category_name}
                        onChange={this.handleChange}
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  <div className="category-form-group">
                    <label htmlFor="categoryImage">Category Image</label>
                    <div className="category-input-wrap">
                      <i className="ph ph-image" />
                      <input
                        id="categoryImage"
                        type="file"
                        className="category-input"
                        accept="image/*"
                        onChange={this.handleFileChange}
                        style={{ padding: "9px 13px 9px 39px" }}
                      />
                    </div>
                    {imagePreview && (
                      <div className="category-icon-live-preview">
                        <span>Preview</span>
                        <div className="category-img-wrap" style={{ border: 0 }}>
                          <img src={imagePreview} alt="Preview" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="category-form-group mb-0">
                    <label>Status</label>
                    <div className="category-status-options">
                      <label
                        className={`status-option ${
                          formData.category_status === "1" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="category_status"
                          value="1"
                          checked={formData.category_status === "1"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-check-circle" /> Active
                        </span>
                      </label>
                      <label
                        className={`status-option ${
                          formData.category_status === "0" ? "selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="category_status"
                          value="0"
                          checked={formData.category_status === "0"}
                          onChange={this.handleChange}
                        />
                        <span>
                          <i className="ph ph-pause-circle" /> Inactive
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="modal-footer category-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} />
                        {modalMode === "add" ? "Add Category" : "Update Category"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Category?</h5>
                <p>
                  This category will be permanently removed. This action cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Categories;